import React from 'react';
import { Flex, Select, Tooltip, Typography } from 'antd';
import refreshIcon from '../../../assets/images/refreshIcon.svg';
import SetupSensorIcon from '../../../assets/images/SetupSensorIcon.jsx';
import SensorInfoIcon from '../../../assets/images/SensorInfoIcon.jsx';
import SensorInfoIconDisabled from '../../../assets/images/SensorInfoIconDisabled.jsx';
import { AlertRule } from './AlertThresholdConfig';

const { Text } = Typography;

/**
 * Map of threshold values keyed by rule ID
 */
type ThresholdsMap = Record<string, string | number>;

/**
 * Team option for dropdown selection
 */
interface TeamOption {
  label: string;
  value: string;
  buttonDisabled?: boolean;
}

/**
 * Props for the AlertThresholdInput component
 */
interface ThresholdInputProps {
  /** Array of rule IDs that require sensor setup */
  setupSensorRuleIds: string[];
  /** Available team options for selection */
  teamsOptions: TeamOption[];
  /** Threshold values for all teams */
  allTeamThresholds: ThresholdsMap;
  /** Current threshold values */
  thresholds: ThresholdsMap;
  /** Alert rule configuration */
  rule: AlertRule;
  /** Current threshold value */
  value: string | number;
  /** Callback when threshold value changes */
  onChange: (ruleId: string, value: string | number) => void;
  /** Default threshold values */
  defaultThresholds: ThresholdsMap;
  /** Navigation function from react-router */
  navigate: (path: string) => void;
  /** Whether sensor setup is required */
  setupSensor: boolean;
  /** Currently selected team (can be 'all', team ID string, or array of team IDs) */
  selectedTeam: string | string[];
  /** Whether the input is disabled */
  disabled: boolean;
}

/**
 * Input component for configuring alert threshold values.
 * Displays threshold rules with dropdown selectors, reset functionality, and sensor setup options.
 *
 * @param setupSensorRuleIds - Array of rule IDs that require sensor setup
 * @param teamsOptions - Available team options
 * @param allTeamThresholds - Threshold values for all teams
 * @param thresholds - Current threshold values
 * @param rule - Alert rule configuration
 * @param value - Current threshold value
 * @param onChange - Callback when threshold value changes
 * @param defaultThresholds - Default threshold values
 * @param navigate - Navigation function
 * @param setupSensor - Whether sensor setup is required
 * @param selectedTeam - Currently selected team
 * @param disabled - Whether the input is disabled
 */
const ThresholdInput: React.FC<ThresholdInputProps> = ({
  setupSensorRuleIds,
  teamsOptions,
  allTeamThresholds,
  thresholds,
  rule,
  value,
  onChange,
  defaultThresholds,
  navigate,
  setupSensor,
  selectedTeam,
  disabled,
}) => {
  const getDropdownOptions = (unit: string): string[] => {
    switch (unit) {
      case '%':
        return Array.from({ length: 11 }, (_, i) => `${i * 10} `);
      case 'mins':
        return Array.from({ length: 13 }, (_, i) => `${i * 10} `);
      case 'ft':
        return Array.from({ length: 21 }, (_, i) => `${i * 100} `);
      case 'Level':
        return ['L1', 'L2', 'L3', 'L4', 'L5'];
      case 'X':
        return Array.from(
          { length: 18 },
          (_, i) => `${(1.5 + i * 0.5).toFixed(1)}`
        );
      default:
        return [];
    }
  };

  const goDefault = selectedTeam?.length === 0 || selectedTeam === 'all';
  const compareAgainst = goDefault ? defaultThresholds : allTeamThresholds;
  const showResetIcon =
    rule.showResetIcon && thresholds?.[rule.id] !== compareAgainst?.[rule.id];
  const shouldDisableReset =
    selectedTeam?.length === 0 && teamsOptions.length !== 0;

  return (
    <Flex
      align='flex-start'
      justify='flex-start'
      style={{ marginBottom: 24, gap: 24 }}
    >
      <Flex vertical style={{ paddingRight: 20, width: 500, flexShrink: 0 }}>
        <Flex align='center' gap={4}>
          <Text strong style={{ fontSize: 14, lineHeight: '16px' }}>
            {rule.title}
          </Text>

          {setupSensorRuleIds.includes(rule.id) ? (
            <div className='icon-wrapper'>
              <Tooltip
                title={
                  rule.infoIconTooltip ||
                  'Sensor required: The correct sensor must be connected to enable this alert'
                }
              >
                {setupSensor === true ? (
                  <SensorInfoIconDisabled className='svg-icon gray-icon hover-yellow' />
                ) : (
                  <SensorInfoIcon className='svg-icon gray-icon hover-blue' />
                )}
              </Tooltip>
            </div>
          ) : rule.showSensorInfoIcon ||
            rule.showSensorInfoIconDisabled ||
            rule.showSetupSensorIcon ? (
            <>
              {rule.showSensorInfoIcon && (
                <div className='icon-wrapper'>
                  <Tooltip title='Sensor enabled'>
                    <SensorInfoIcon className='svg-icon gray-icon hover-blue' />
                  </Tooltip>
                </div>
              )}

              {rule.showSensorInfoIconDisabled && (
                <div className='icon-wrapper'>
                  <Tooltip title='Sensor required: The correct sensor must be connected to enable this alert'>
                    <SensorInfoIconDisabled className='svg-icon gray-icon hover-yellow' />
                  </Tooltip>
                </div>
              )}

              {rule.showSetupSensorIcon && (
                <div className='icon-wrapper'>
                  <Tooltip title='Critical alert: An alert that is indicative of a potential product compromise'>
                    <SetupSensorIcon className='svg-icon gray-icon hover-red' />
                  </Tooltip>
                </div>
              )}
            </>
          ) : null}

          {rule.sensorNote && (
            <Text
              className='dotted-border-spaced'
              style={{
                fontSize: 12,
                color: 'black',
                //  borderTopWidth: 0,
                //  borderRightWidth: 0,
                //  borderLeftWidth: 0,
                //  borderBottomWidth: 1,
                //  borderBottomColor: "black",
                //  borderStyle: "dotted",
              }}
            >
              {rule.sensorNote}
            </Text>
          )}
        </Flex>

        <Text
          type='secondary'
          style={{
            fontSize: 12,
            color: 'var(--text-secondary)',
            lineHeight: '20px',
          }}
        >
          {rule.description}
        </Text>
      </Flex>
      <Flex align='center' gap={8}>
        {rule.conditionType ? (
          <Text
            style={{
              fontSize: 12,
              color: 'var(--text-secondary)',
              width: 40,
              lineHeight: '20px',
              whiteSpace: 'nowrap',
              textAlign: 'right',
            }}
          >
            {rule.conditionType}
          </Text>
        ) : (
          <div style={{ width: 40 }} />
        )}

        <Flex
          align='center'
          justify='space-between'
          style={{
            background: '#F5F5F5',
            borderRadius: '8px',
            padding: '6px 12px',
            width: 112,
            height: 36,
            flexShrink: 0,
          }}
        >
          <Select
            className='inputThreshold'
            disabled={
              rule.showSetupSensorButton ||
              disabled ||
              value === 'NA' ||
              (selectedTeam.length === 0 && teamsOptions.length !== 0)
            }
            value={value?.toString()}
            style={{
              width: 80,
              backgroundColor: 'transparent',
              border: 'none',
              textAlign: 'left',
            }}
            options={getDropdownOptions(rule.unit).map((option) => ({
              label: option,
              value: option,
            }))}
            onChange={(val) => {
              onChange(rule.id, val);
            }}
            suffixIcon={null}
          />
          <Text
            strong
            disabled={
              (selectedTeam.length === 0 && teamsOptions.length != 0) ||
              rule.showSetupSensorButton
            }
            style={{
              fontSize: 14,
              whiteSpace: 'nowrap',
              textAlign: 'right',
            }}
          >
            {rule.unit}
          </Text>
        </Flex>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: showResetIcon ? 36 : 0,
            height: showResetIcon ? 36 : 0,
            cursor: !disabled && showResetIcon ? 'pointer' : 'default',
            opacity: disabled || shouldDisableReset ? 0.5 : 1,
          }}
          onClick={() => {
            if (!disabled && showResetIcon && !shouldDisableReset) {
              const fallbackValue = compareAgainst?.[rule.id];
              if (fallbackValue !== undefined) {
                onChange(rule.id, fallbackValue);
              }
            }
          }}
        >
          {showResetIcon && (
            <Tooltip title='Reset to default'>
              <img
                src={refreshIcon}
                alt='reset'
                width={36}
                height={36}
                style={{ opacity: disabled ? 0.3 : 1 }}
              />
            </Tooltip>
          )}
        </div>

        {rule.showSetupSensorButton && (
          <div
            onClick={() => {
              if (!disabled && !shouldDisableReset) {
                navigate('/settings/sensor');
              }
            }}
            style={{
              height: 28,
              border: '1px solid #AFAFAF',
              borderRadius: 8,
              padding: '0 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 12,
              color: disabled ? '#AFAFAF' : '#5E5E5E',
              fontWeight: 500,
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled || shouldDisableReset ? 0.5 : 1,
            }}
          >
            Setup sensor
          </div>
        )}
      </Flex>
    </Flex>
  );
};
export default ThresholdInput;
